import {
  Input,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
} from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { Component } from '@angular/core'
import { Observable, Subscription, interval } from 'rxjs'
import { take } from 'rxjs/operators'
import { Live, WatchData } from 'src/lib/entity/live'
import { environment } from 'src/env/environment'
import { Player, Source } from '@castify-inc/castify-player'
import { LiveUserAuthority } from 'src/lib/entity/live-user-authority'
import { Mag2LocalStorage } from 'src/lib/mag2-local-storage'
import { PlayerStatus } from 'src/lib/player/player'
import { PlayerHLS } from 'src/lib/player/player-hls'
import { PlayerNative } from 'src/lib/player/player-native'
import { MagliveApiClient } from 'src/lib/api-client/maglive-api-client'
import { isSupported as hlsIsSupported } from 'hls.js'
import { CookieService } from 'ngx-cookie-service'
import { ConsoleLogger } from 'src/lib/console-logger'
import { GiftAnimationReq } from 'src/lib/entity/gift-point'
import { VARIANTS } from 'src/lib/constants'
import { GiftAnimationComponent } from './gift-animation/gift-animation.component'

if (environment.baseURL.castify !== undefined) {
  Source.server = environment.baseURL.castify
}

@Component({
  selector: 'maglive-player',
  templateUrl: './player.component.html',
  styleUrls: ['./player.component.scss'],
})
export class PlayerComponent implements OnInit, OnDestroy, AfterViewInit {
  constructor(
    private route: ActivatedRoute,
    private cookieService: CookieService,
    private logger: ConsoleLogger,
  ) {}

  /* Subscription */
  private queryParamsSub: Subscription | undefined = undefined
  private broadcastDataSub: Subscription | undefined = undefined
  private muteIntervalSub: Subscription | undefined = undefined

  player: Player
  hlsPlayer: PlayerHLS
  nativePlayer: PlayerNative
  private broadcastData: WatchData | 'NOT_FOUND' | undefined = undefined
  status: PlayerStatus = 'loading'
  private token: string | undefined
  isAws: boolean

  private broadcastDataObs: Observable<WatchData | 'NOT_FOUND'> | undefined =
    undefined

  private cookieKey: string
  private expireDay = 1

  muted = true
  _live: Live

  // TODO: room_config_idが変わればこのリストも変わってくる = ngOnInitでroom_config_idの値を確認して、variantsを調整する必要が今後出てくる
  variants = VARIANTS

  @ViewChild('video', { static: true }) video: ElementRef<HTMLVideoElement>
  @ViewChild(GiftAnimationComponent) giftAnimationComponent: GiftAnimationComponent

  @Input() authority: LiveUserAuthority
  @Input() set live(live: Live | string) {
    if (typeof live === 'string') {
      return
    }
    const previousLive = this._live
    this._live = live
    this.play(live, previousLive, undefined)
  }
  @Input() set isaws(isaws: boolean) {
    this.isAws = isaws
  }
  @Input() selectedVariant: string

  ngOnInit() {
    this.playerCheck()
  }

  ngOnDestroy() {
    if (this.player) {
      this.player.reset()
      this.player = null
    } else if (this.hlsPlayer) {
      this.hlsPlayer.destroy()
      this.hlsPlayer = null
    } else if (this.nativePlayer) {
      this.nativePlayer.destroy()
      this.nativePlayer = null
    }
    if (this.broadcastData) {
      this.broadcastData = undefined
    }
    if (this.broadcastDataSub) {
      this.broadcastDataSub.unsubscribe()
    }
    if (this.muteIntervalSub) {
      this.muteIntervalSub.unsubscribe()
    }
    if (this.queryParamsSub) {
      this.queryParamsSub.unsubscribe()
    }
  }

  ngAfterViewInit() {
    // 再生再開時にミュートになる問題の暫定対応
    this.muteIntervalSub = interval(200).subscribe(() => {
      if (!this.muted && this.video.nativeElement.muted) {
        // console.error("mute property is conflicted."); // 紛らわしいのでコメントアウト
        this.video.nativeElement.muted = false
      }
    })
  }

  changeVariant(variant: string) {
    this.selectedVariant = variant
    if (variant === 'auto') {
      this.autoPlay()
    } else {
      this.selectPlay(variant)
    }
  }

  autoPlay() {
    // TODO: destroy関係はまとめたい
    if (this.isAws) {
      if (hlsIsSupported()) {
        this.hlsPlayer.destroy()
        this.hlsPlay(this._live, undefined, undefined)
      } else {
        this.nativePlayer.destroy()
        this.nativePlay(this._live, undefined, undefined)
      }
    } else {
      this.play(this._live, undefined, undefined)
    }
  }

  selectPlay(variant: string) {
    if (this.isAws) {
      if (hlsIsSupported()) {
        this.hlsPlayer.destroy()
        this.hlsPlay(this._live, undefined, variant)
      } else {
        this.nativePlayer.destroy()
        this.nativePlay(this._live, undefined, variant)
      }
    } else {
      this.play(this._live, undefined, variant)
    }
  }

  changeHlsPrefix(name: string): string {
    const variant = this.variants.find((item) => item.value === name)
    return variant.prefix
  }

  play(live: Live, previousLive: Live | undefined, name: string | undefined) {
    if (!this.video) {
      return
    }
    if (!this.player) {
      // テンプレートから set live が呼ばれた場合は ngOnInit より先に呼ばれるため未設定
      return
    }

    if (this.isPreviousLive(previousLive, live)) return

    if (live.status === 'ON_AIR') {
      this.queryParamsSub = this.route.queryParams.subscribe((params) => {
        this.token = params.token
      })
      const customData = JSON.stringify({
        clientId: Mag2LocalStorage.getClientId(),
        sessionId:
          this.authority && this.authority.sessionId
            ? this.authority.sessionId
            : '',
        userId: Mag2LocalStorage.getUserId(),
        token: this.token,
      })
      if (name === undefined) {
        this.player.source = new Source(live.broadcastId, {
          key: Mag2LocalStorage.getClientId(),
          val: customData,
        })
      } else {
        this.player.source = new Source(live.broadcastId, {
          key: Mag2LocalStorage.getClientId(),
          val: customData,
          name: name,
        })
      }

      this.player.source
        .load()
        .then((_) => {
          if (!this.player) {
            return
          }
          this.status = 'played'
          this.video.nativeElement.muted = true
          this.player.seek('live')
        })
        .catch((_) => {
          this.status = 'cannot_view'
        })
    } else {
      this.player.paused = true
    }
  }

  hlsPlay(
    live: Live,
    previousLive: Live | undefined,
    name: string | undefined
  ) {
    let changeHlsEndpoint: string | undefined = undefined
    // TODO: 一旦play()から持ってきておく。共通化などは後から行う
    if (!this.video) {
      return
    }

    if (!this.hlsPlayer) {
      // テンプレートから set live が呼ばれた場合は ngOnInit より先に呼ばれるため未設定
      return
    }

    if (this.broadcastData === 'NOT_FOUND') {
      console.error('NOT_FOUND')
      return
    }

    if (live.status === 'RESERVED') {
      return
    }

    if (this.isPreviousLive(previousLive, live)) return

    if (name !== undefined) {
      // ~~index_0.m3u8が画質が高く、~~index_2.m3u8ファイルが画質が低い
      const prefix = this.changeHlsPrefix(name)
      changeHlsEndpoint = this.broadcastData.hlsEndpoint.replace(
        '.m3u8',
        `${prefix}.m3u8`
      )
    }

    // cookieにmute状態が保存されていたらcookieを参照してmuteをon/offする
    if (this.cookieService.check(this.cookieKey)) {
      const isMuted = this.toBoolean(this.cookieService.get(this.cookieKey))
      this.logger.info(this.constructor.name, 'hlsPlay', 'isMuted', isMuted)
      if (!isMuted) {
        this.video.nativeElement.muted = this.muted = !this.muted
      }
    }

    // if (live.status === 'ON_AIR') {
    this.hlsPlayer.play(
      changeHlsEndpoint ? changeHlsEndpoint : this.broadcastData.hlsEndpoint, // 画質が調整された場合、hlsEndpointは変更後のものを使用
      undefined,
      undefined
    )
    // this.muted = false; // TODO: castifyでは再生を開始してもmuteを解除してください、と出ていたため問題ないと思うが・・・念のため確認する！
    // this.hlsPlayer.seek("live");
    this.video.nativeElement.addEventListener('canplay', () => {
      this.status = 'played'
    })
    // } else {
    //   this.hlsPlayer.pause(true);
    // }
  }

  nativePlay(
    live: Live,
    previousLive: Live | undefined,
    name: string | undefined
  ) {
    let changeHlsEndpoint: string | undefined = undefined
    // TODO: 一旦play()から持ってきておく。共通化などは後から行う
    if (!this.video) {
      return
    }

    if (!this.nativePlayer) {
      // テンプレートから set live が呼ばれた場合は ngOnInit より先に呼ばれるため未設定
      return
    }

    if (this.broadcastData === 'NOT_FOUND') {
      console.error('NOT_FOUND')
      return
    }

    if (live.status === 'RESERVED') {
      return
    }

    if (this.isPreviousLive(previousLive, live)) return

    if (name !== undefined) {
      // ~~index_0.m3u8が画質が高く、~~index_2.m3u8ファイルが画質が低い
      const prefix = this.changeHlsPrefix(name)
      changeHlsEndpoint = this.broadcastData.hlsEndpoint.replace(
        '.m3u8',
        `${prefix}.m3u8`
      )
    }

    // cookieにmute状態が保存されていたらcookieを参照してmuteをon/offする
    if (this.cookieService.check(this.cookieKey)) {
      const isMuted = this.toBoolean(this.cookieService.get(this.cookieKey))
      if (!isMuted) {
        this.video.nativeElement.muted = this.muted = !this.muted
      }
    }

    // NOTE: 遅延処理がある為、ここではlive.statusで判定しない
    // if (live.status === 'ON_AIR') {
    this.nativePlayer.play(
      changeHlsEndpoint ? changeHlsEndpoint : this.broadcastData.hlsEndpoint, // 画質が調整された場合、hlsEndpointは変更後のものを使用
      undefined,
      undefined
    )
    // this.muted = false; // TODO: castifyでは再生を開始してもmuteを解除してください、と出ていたため問題ないと思うが・・・念のため確認する！
    this.video.nativeElement.addEventListener('canplay', () => {
      this.status = 'played'
    })

    // } else {
    //   this.hlsPlayer.pause(true);
    // }
  }

  pause(): boolean {
    if (!this.video) {
      return
    }
    if (!this.player) {
      return
    }
    this.status = 'stopped'
    return (this.player.paused = true)
  }

  destroy() {
    if (!this.player) {
      return
    }
    this.status = 'unloaded'
    this.player.reset()
  }

  playerCheck() {
    // isAwsがtrueでも旧基盤での予約分はcastify視聴にする必要がある
    if (
      this._live.isNewSystem === undefined ||
      this._live.isNewSystem === null
    ) {
      this.isAws = false
    }

    if (this.isAws) {
      this.cookieKey = this._live.id
      // AWS media service
      this.broadcastDataObs = MagliveApiClient.getWatchData(this._live.id)
      // 何度も入室APIを叩かないようtakeを使用する
      this.broadcastDataSub = this.broadcastDataObs
        .pipe(take(1))
        .subscribe((val) => {
          this.broadcastData = val
          if (hlsIsSupported()) {
            this.hlsPlayer = new PlayerHLS(this.video)
            this.hlsPlay(this._live, undefined, undefined)
          } else {
            // hlsをサポートしていないiOSのsafari等用
            this.nativePlayer = new PlayerNative(this.video)
            this.nativePlay(this._live, undefined, undefined)
          }
        })
    } else {
      // castify
      this.player = new Player(this.video.nativeElement)
      this.play(this._live, undefined, undefined)
    }
  }

  // Volume
  //~~~

  toggleMute() {
    this.video.nativeElement.muted = this.muted = !this.muted
    // 遅延処理でmuteになってしまう為、cookieに一時保存する
    let value = this.muted.toString()
    this.cookieService.set(this.cookieKey, value, { expires: this.expireDay })
    this.logger.info(
      this.constructor.name,
      'toggleMute',
      'cookie',
      this.cookieService.get(this.cookieKey)
    )
  }

  private toBoolean(data) {
    return data.toLowerCase() === 'true'
  }

  private isPreviousLive(previousLive, live): boolean {
    if (
      previousLive &&
      live.id === previousLive.id &&
      live.status === previousLive.status &&
      live.options.isViewerCountEnabled ===
        previousLive.options.isViewerCountEnabled &&
      live.options.secondsViewWithoutAuthority ===
        previousLive.options.secondsViewWithoutAuthority &&
      live.options.viewerSegment === previousLive.options.viewerSegment
    ) {
      this.logger.message(
        this.constructor.name,
        'isPreviousLive',
        `new live is exactly same as previous. id: ${live.id}`
      )
      return true
    } else {
      return false
    }
  }

  setAnimation(request: GiftAnimationReq) {
    this.giftAnimationComponent.setAnimation(request)
  }
}
